import React, { useState, useEffect } from "react"
import { IfFulfilled, IfPending, useAsync } from "react-async"
import crossfilter from "crossfilter2"
import moment from "moment"
import { makeXY } from "./crossfilter-util"
import { keyBy } from "lodash"

import DeathsStateChart from "./us-deaths-by-state-chart"
import DeathsLineChart from "./us-deaths-line-chart"
import CasesNumTests from "./us-cases-num-tests"
import CasePercentage from "./us-cases-percentage"

const fetchByStateData = async () => {
  const response = await fetch(
    "https://api.covidtracking.com/api/v1/states/daily.json",
    {
      headers: {
        Accept: "application/json",
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  )
  const responseBody = await response.json()
  responseBody.forEach(
    it => (it["dateChecked"] = moment(it["dateChecked"]).format("YYYY-MM-DD"))
  )
  const data = crossfilter(responseBody)

  const deaths = data.dimension(d => d["death"])
  const state = data.dimension(d => d["state"])
  const date = data.dimension(d => d["dateChecked"])
  const dates = date.group(d => d)
  const states = state.group(d => d)
  state.filter(["CA"])
  return { cf: data, deaths, state, states, dates, date }
}

const transformDataStates = data => {
  const { states } = data
  const kv = states.reduceSum(d => d.deathIncrease).all()
  const locations = kv.map(d => d.key)
  const z = kv.map(d => d.value)

  return [
    {
      locations,
      z,
      type: "choropleth",
      locationmode: "USA-states",
      name: "Deaths",
    },
  ]
}

// california death rate has a ~43% deaths in nursing homes
const transformDataByDate = data => {
  const { dates } = data
  const kv = dates
    .reduceSum(d => Math.floor(d.deathIncrease * (1 - 0.43)))
    .all()
  const xy = makeXY(kv)
  return xy
}

const transformDeathsInNursingHomes = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => Math.floor(d.deathIncrease * 0.43)).all()
  const xy = makeXY(kv)
  return xy
}

const transformDataCases = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => d.positiveIncrease).all()
  const xy = makeXY(kv)

  return xy
}

const tranformCasesPercent = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => d.positiveIncrease).all()

  const xy = makeXY(kv)
  return xy
}

const transformDataCasesNegative = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => -d.negativeIncrease).all()
  const xy = makeXY(kv)

  return xy
}

const transformDataCasesNegativePercent = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => d.negativeIncrease).all()
  const kvTotal = dates.reduceSum(d => d.totalTestResultsIncrease).all()

  const kvLookup = keyBy(kvTotal, "key")

  const kvPercent = kv.map(it => {
    debugger
    return {
      key: it.key,
      value: ((1.0 * it.value) / kvLookup[it.key].value) * 100.0,
    }
  })

  const xy = makeXY(kvPercent)

  return xy
}

const transformNumTests = data => {
  const { dates } = data
  const kv = dates.reduceSum(d => d.totalTestResultsIncrease).all()
  const xy = makeXY(kv)

  return xy
}

const CrossfilteredCharts = () => {
  const dataByState = useAsync(fetchByStateData)
  const [deathsByStateData, setDeathsByStateData] = useState([])
  const [deathsByDateData, setDeathsByDateData] = useState({ x: [], y: [] })
  const [deathsByDateInNursing, setDeathsByDateInNursing] = useState({
    x: [],
    y: [],
  })
  const [selectedStates, setSelectedStates] = useState([])
  const [cases, setCases] = useState({ x: [], y: [] })
  const [casesPercent, setCasesPercent] = useState({ x: [], y: [] })
  const [numTests, setNumTests] = useState({ x: [], y: [] })
  const [casesNegative, setCasesNegative] = useState({ x: [], y: [] })
  const [casesNegativePercent, setCasesNegativePercent] = useState({
    x: [],
    y: [],
  })

  useEffect(() => {
    if (!dataByState.data) return
    setDeathsByStateData(transformDataStates(dataByState.data))
    setDeathsByDateData(transformDataByDate(dataByState.data))
    const cases = transformDataCases(dataByState.data)
    const casesNegative = transformDataCasesNegative(dataByState.data)
    const tests = transformNumTests(dataByState.data)
    setDeathsByDateInNursing(transformDeathsInNursingHomes(dataByState.data))
    setCases(cases)
    setCasesNegative(casesNegative)
    setNumTests(tests)

    const percentCases = cases.y.map((d, index) => (d / tests.y[index]) * 100)
    const percentNegative = casesNegative.y.map(
      (d, index) => (-d / tests.y[index]) * 100
    )

    setCasesPercent({ ...tests, y: percentCases })
    setCasesNegativePercent({ ...tests, y: percentNegative })
  }, [dataByState.data])

  useEffect(() => {
    if (!dataByState.data) return
    const { state } = dataByState.data
    state.filter(selectedStates[0])
    setDeathsByStateData(transformDataStates(dataByState.data))
    setDeathsByDateData(transformDataByDate(dataByState.data))
  }, [selectedStates])

  return (
    <>
      <IfPending state={dataByState}>Loading...</IfPending>
      <IfFulfilled state={dataByState}>
        {/*<DeathsStateChart*/}
        {/*  data={deathsByStateData}*/}
        {/*  setSelectedStates={setSelectedStates}*/}
        {/*/>*/}
        <DeathsLineChart
          data={[
            {
              ...deathsByDateData,
              type: "bar",
              stackgroup: "one",
              name: "Outside of Nursing Homes",
              line: { shape: "spline" },
            },
            {
              ...deathsByDateInNursing,
              type: "bar",
              stackgroup: "one",
              name: "In Nursing Homes (45%)",
              line: { shape: "spline" },
            },
          ]}
        />
        <CasesNumTests
          cases={{ ...cases, type: "bar", name: "Cases" }}
          numTests={{
            ...numTests,
            type: "scatter",
            mode: "lines+markers",
            name: "Tests",
          }}
        />
        <CasePercentage
          positiveCasePercent={{
            ...casesPercent,
            type: "bar",
            name: "Positive (%)",
          }}
          negativeCasePercent={{
            ...casesNegativePercent,
            type: "bar",
            name: "Negitive (%)",
          }}
          positiveCase={{
            ...cases,
            type: "scatter",
            mode: "lines+markers",
            name: "Cases",
          }}
          negativeCase={{
            ...casesNegative,
            type: "scatter",
            mode: "lines+markers",
            name: "Negative Cases",
          }}
        />
      </IfFulfilled>
    </>
  )
}

export default CrossfilteredCharts
